import {makeAutoObservable, runInAction} from 'mobx';
import professionalModel from "../../../model/professional/professional-model";
import companyCollaboratorsViewModel from "./company-collaborators-view-model";
import {
  AvailableRolesAndPermissions,
  CollaboratorEditViewData,
  Permission
} from "../../../model/professional/professional-model";
import companyStore from "../../../store/company-store";


export class CompanyCollaboratorEditViewModel {

  data: CollaboratorEditViewData | null = null;
  availableRolesAndPermissions: AvailableRolesAndPermissions | null = null;

  selectedRoleId: string | null = null;
  selectedPermissionIds: string[] =  [];
  isSubmitting: boolean = false;

  isLoading: boolean = false;
  error: string | null = null;

  permissionEditMode: 'add' | 'remove' | null = null;
  tempSelectedPermissionIds: string[] = [];

   vmCompanyCollaboratorsViewModel =  companyCollaboratorsViewModel;


  private static instance: CompanyCollaboratorEditViewModel;

  private constructor() {
    makeAutoObservable(this);
  }

  public static getInstance(): CompanyCollaboratorEditViewModel {
    if (!CompanyCollaboratorEditViewModel.instance) {
      CompanyCollaboratorEditViewModel.instance = new CompanyCollaboratorEditViewModel();
    }
    return CompanyCollaboratorEditViewModel.instance;
  }

  setData(data: CollaboratorEditViewData) {
    this.data = data;
    this.selectedRoleId = data.role.id;
    this.selectedPermissionIds = data.permissions.map(p => p.id);
  }

  setAvailableRolesAndPermissions(rolesAndPermissions: AvailableRolesAndPermissions) {
    this.availableRolesAndPermissions = rolesAndPermissions;
  }

  setSelectedRoleId(roleId: string) {
    this.selectedRoleId = roleId;
  }

  setSelectedPermissionIds(permissionIds: string[]) {
    this.selectedPermissionIds = permissionIds;
  }

  setIsSubmitting(submitting: boolean) {
    this.isSubmitting = submitting;
  }

  setPermissionEditMode(mode: 'add' | 'remove' | null) {
    this.permissionEditMode = mode;
    this.tempSelectedPermissionIds = [];
  }

  setTempSelectedPermissionIds(permissionIds: string[]) {
    this.tempSelectedPermissionIds = permissionIds;
  }

  get groupedPermissions(): Record<string, Permission[]> {
    const groupedPermissions: Record<string, Permission[]> = {};
    if (!this.availableRolesAndPermissions || !this.availableRolesAndPermissions.permissions) {
      return groupedPermissions;
    }
    this.availableRolesAndPermissions.permissions.forEach(permission => {
      const parts = permission.name.split(' ');
      if (parts.length > 0) {
        const action = parts[0];

        if (!groupedPermissions[action]) {
          groupedPermissions[action] = [];
        }
        groupedPermissions[action].push(permission);
      }
    });

    return groupedPermissions;
  }

  isPermissionSelected = (permissionId: string): boolean => {
    return this.selectedPermissionIds.includes(permissionId);
  };

  isTempPermissionSelected = (permissionId: string): boolean => {
    return this.tempSelectedPermissionIds.includes(permissionId);
  };

  // Vérifier si une permission doit être grisée selon le mode
  isPermissionDisabled = (permissionId: string): boolean => {
    if (this.permissionEditMode === 'add') {
      // Mode "Accorder des droits" : griser les permissions déjà accordées
      return this.isPermissionSelected(permissionId);
    } else if (this.permissionEditMode === 'remove') {
      // Mode "Supprimer des droits" : griser les permissions non accordées
      return !this.isPermissionSelected(permissionId);
    }
    return false;
  };

  toggleTempPermission = (permissionId: string) => {
    if (this.isPermissionDisabled(permissionId)) {
      return;
    }

    if (this.isTempPermissionSelected(permissionId)) {
      this.setTempSelectedPermissionIds(this.tempSelectedPermissionIds.filter(id => id !== permissionId));
    } else {
      this.setTempSelectedPermissionIds([...this.tempSelectedPermissionIds, permissionId]);
    }
  };

  togglePermission = (permissionId: string) => {
    if (this.isPermissionSelected(permissionId)) {
      this.setSelectedPermissionIds(this.selectedPermissionIds.filter(id => id !== permissionId));
    } else {
      this.setSelectedPermissionIds([...this.selectedPermissionIds, permissionId]);
    }
  };

  toggleTempPermissionGroup = (permissions: Permission[]) => {
    const availablePermissions = permissions.filter(p => !this.isPermissionDisabled(p.id));
    const availablePermissionIds = availablePermissions.map(p => p.id);
    const allSelected = availablePermissions.every(p => this.isTempPermissionSelected(p.id));

    if (allSelected) {
      this.setTempSelectedPermissionIds(this.tempSelectedPermissionIds.filter(id => !availablePermissionIds.includes(id)));
    } else {
      const newIds = availablePermissionIds.filter(id => !this.tempSelectedPermissionIds.includes(id));
      this.setTempSelectedPermissionIds([...this.tempSelectedPermissionIds, ...newIds]);
    }
  };

  togglePermissionGroup = (groupName: string, permissions: Permission[]) => {
    const permissionIds = permissions.map(p => p.id);
    const allSelected = permissions.every(p => this.isPermissionSelected(p.id));

    if (allSelected) {
      this.setSelectedPermissionIds(this.selectedPermissionIds.filter(id => !permissionIds.includes(id)));
    } else {
      const newIds = permissionIds.filter(id => !this.selectedPermissionIds.includes(id));
      this.setSelectedPermissionIds([...this.selectedPermissionIds, ...newIds]);
    }
  };

  handleAddPermissions = async () => {
    if (this.tempSelectedPermissionIds.length === 0 || !this.data) return;

    this.setIsSubmitting(true);

    try {
      const company = companyStore.getCompanyById;
      if (!company) return;

      const permissionsToAdd = this.tempSelectedPermissionIds.map(permissionId => ({
        userId: this.data!.collaborator.id,
        permissionId: permissionId,
        companyId: company.id
      }));

      await professionalModel.handleGrantPermissions(permissionsToAdd);

      const combinedPermissions = [...this.selectedPermissionIds, ...this.tempSelectedPermissionIds];
      const updatedPermissions = Array.from(new Set(combinedPermissions));
      this.setSelectedPermissionIds(updatedPermissions);

      this.setPermissionEditMode(null);
    } catch (error) {
      console.error('Erreur lors de l\'ajout des permissions:', error);
    } finally {
      this.setIsSubmitting(false);
    }
  };

  handleRemovePermissions = async () => {
    if (this.tempSelectedPermissionIds.length === 0 || !this.data) return;

    this.setIsSubmitting(true);

    try {
      const company = companyStore.getCompanyById;
      if (!company) return;

      const permissionsToRemove = this.tempSelectedPermissionIds.map(permissionId => ({
        userId: this.data!.collaborator.id,
        permissionId: permissionId,
        companyId: company.id
      }));

      await professionalModel.handleRevokePermissions(permissionsToRemove);

      const updatedPermissions = this.selectedPermissionIds.filter(
        id => !this.tempSelectedPermissionIds.includes(id)
      );
      this.setSelectedPermissionIds(updatedPermissions);

      this.setPermissionEditMode(null);
    } catch (error) {
      console.error('Erreur lors de la suppression des permissions:', error);
    } finally {
      this.setIsSubmitting(false);
    }
  };

  handleCancelPermissionEdit = () => {
    this.setPermissionEditMode(null);
  };

  async loadCollaboratorData() {
    this.isLoading = true;
    this.error = null;

    try {
      const company = companyStore.getCompanyById;

      if(!company) return
      const response = await professionalModel.handleGetDataCollaboratorByCompany(company?.id, this.vmCompanyCollaboratorsViewModel.selectedCollaborator.collaboratorId)
        this.setData(response)
      runInAction(() => {
        this.isLoading = false;
      });

    } catch (error) {
      runInAction(() => {
        this.error = "Erreur lors du chargement des données du collaborateur";
        this.isLoading = false;
      });
      console.error("Erreur API loadCollaboratorData:", error);
      throw error;
    }
  }

  async loadRolesAndPermissions() {
    this.isLoading = true;
    this.error = null;

    try {
      const response = await professionalModel.handleGetDataRolesAndPermissions()

      this.setAvailableRolesAndPermissions(response)

      runInAction(() => {
        this.isLoading = false;
      });
    } catch (error) {
      runInAction(() => {
        this.error = "Erreur lors du chargement des rôles et permissions";
        this.isLoading = false;
      });
      console.error("Erreur API loadRolesAndPermissions:", error);
      throw error;
    }
  }

  async initialize() {
    try {
      await Promise.all([
        this.loadCollaboratorData(),
        this.loadRolesAndPermissions()
      ]);
    } catch (error) {
      console.error("Erreur d'initialisation:", error);
    }
  }

  getRoleBadgeClass = (roleName: string): string => {
    switch (roleName) {
      case 'SUPER_ADMIN':
        return 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-300';
      case 'OWNER':
        return 'bg-purple-100 text-purple-800 dark:bg-purple-900 dark:text-purple-300';
      case 'ADMIN':
        return 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-300';
      case 'COLLABORATOR':
        return 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-300';
      case 'STAFF':
        return 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-300';
      default:
        return 'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-300';
    }
  };

  getFormattedName = (firstName: string, lastName: string): string => {
    return `${firstName.charAt(0).toUpperCase() + firstName.slice(1)} ${lastName.charAt(0).toUpperCase() + lastName.slice(1)}`;
  };

  getInitials = (firstName: string, lastName: string): string => {
    return `${firstName.charAt(0)}${lastName.charAt(0)}`.toUpperCase();
  };
}

export default CompanyCollaboratorEditViewModel.getInstance();
